# Terms of Use

Last updated: 17th December 2025

## 1. Overview

These Terms of Use (**“Terms”**) govern access to and use of the Flowstep Platform operated by FlowstepDesign OÜ (**“Flowstep”**, **“we”**, **“us”**). By registering an account or using the Platform, you agree to be bound by these Terms and the Privacy Policy.

These Terms apply to both business users (**Clients**) and private users (**Customers**). Where mandatory consumer protection provisions apply, they will prevail to the extent required by law.

---

## 2. Definitions

- **Client** – a legal entity that registers an account or uses the Platform.
- **Customer** – a natural person that registers an account or uses the Platform.
- **Platform** – the Flowstep platform available via [www.flowstep.ai](http://www.flowstep.ai/) and related features and services.
- **User** – a Client or Customer who uses the Platform.
- **User Content** – any designs, diagrams, text, images, PDFs, graphics, projects, prompts, files, or other materials that Users create, generate, upload, post, share, or otherwise make available through the Platform.
- **Subscription** – a paid plan granting access to specific features, usage entitlements, and limits, as displayed during purchase and in the Platform.

---

## 3. Account Registration and Use

Users must provide accurate and complete information when registering and keep such information up to date. Users are responsible for maintaining the confidentiality of their credentials and for all activities carried out under their account.

Flowstep may reject registrations or suspend or terminate accounts in accordance with these Terms.

---

## 4. Platform Functionality

Flowstep provides the Platform with the functionality available at the time of use and may update, modify, or change the Platform and its features from time to time, including for technical development, security, compliance, or service improvement purposes.

Flowstep is not obliged to monitor User Content or user activity on the Platform.

---

## 5. Pricing and Payments

Some features of the Platform may require a paid Subscription. Subscription fees, billing cadence (monthly or annual), and applicable taxes are displayed during checkout and in the Platform.

Payments are processed via Stripe. Payment card details are provided to Stripe and are not stored by Flowstep.

---

## 6. Subscriptions, Usage Limits, and Entitlements

Flowstep may offer Subscriptions that include feature-based and/or usage-based entitlements (for example, message entitlements where one message equals one prompt), limits, or other conditions as displayed in the Platform.

Flowstep may modify features, usage limits, entitlements, and plan structures between subscription periods and/or for new purchases, to the extent permitted by applicable law. Any changes that legally require notice will be notified accordingly.

---

## 7. Termination

Users may terminate their account at any time via the Platform or by contacting Flowstep.

Flowstep may suspend or terminate access if required by law or if a User materially violates these Terms.

---

## 8. Export of User Content

Users are responsible for exporting or backing up their User Content before terminating their account or before account access is terminated. Flowstep is not responsible for loss of User Content resulting from account termination or deletion performed in accordance with these Terms and the Privacy Policy.

---

## 9. Intellectual Property and User Content

Flowstep retains all intellectual property rights in and to the Platform, including underlying software, databases, and related technology.

Users retain ownership of their User Content. By using the Platform and making User Content available, the User grants Flowstep a non-exclusive, worldwide, royalty-free licence to host, store, process, transmit, display, and format User Content solely to operate and provide the Platform and enable requested features, including collaboration and sharing.

---

## 10. Right to Use Client References

Where the User is a Client (legal entity), the Client grants Flowstep the right to use the Client’s company name and logo as a reference for marketing or promotional purposes related to the Platform, including on Flowstep’s website, within the Platform, and in communications with existing or potential customers, partners, and investors.

Flowstep will not use the Client as a reference if the Client notifies Flowstep by email at [hello@flowstep.ai](mailto:hello@flowstep.ai) that it does not wish to be used as a reference. Upon receiving such notice, Flowstep will cease further use within a reasonable time.

This section does not permit the use of personal data of individual users or Customers.

---

## 11. Acceptable Use

Users must ensure that their use of the Platform and User Content complies with applicable laws and does not infringe the rights of others.

In particular, User Content must not:

- be unlawful, fraudulent, or deceptive
- infringe intellectual property or other proprietary rights
- contain malware or malicious code
- be defamatory, harassing, or threatening
- promote violence, hatred, or discrimination
- facilitate illegal activities

Flowstep may restrict access to content or suspend accounts where necessary to enforce these Terms or comply with legal obligations.

---

## 12. Liability

The Platform is provided “as is” and “as available”. To the maximum extent permitted by applicable law, Flowstep disclaims warranties and shall not be liable for indirect or consequential damages. Nothing in these Terms limits liability that cannot be excluded under applicable law.

---

## 13. Governing Law, Dispute Resolution, and Miscellaneous

These Terms are governed by the laws of the Republic of Estonia. Disputes shall be resolved in Harju County Court, unless mandatory consumer protection rules provide otherwise.

If any provision of these Terms is held invalid or unenforceable, the remaining provisions remain in force.

Flowstep may amend these Terms with prior notice where required. Continued use of the Platform after changes take effect constitutes acceptance of the updated Terms.
